import { NameDto } from 'api/Common/Types'

import { AuditLog } from '../../api/Clients/Types/AuditLog'
import { BankValidationDto } from '../../api/NPayments/Types/BankValidationDto'
import {
  AddClientBankAccount,
  ClientBankAccount,
  ClientBankAccountStatus,
  UpdateClientBankAccount,
} from '../../api/NPayments/Types/ClientBankAccount'
import { NPaymentProviderDto } from '../../api/NPayments/Types/NPaymentProviderDto'
import { NPaymentProviderOrderDto } from '../../api/NPayments/Types/NPaymentProviderOrderDto'
import {
  TokenizedCardDetails,
  UpdateTokenizedCardholderName,
  UpdateTokenizedCardStatus,
} from '../../api/NPayments/Types/TokenizedCardDetails'
import { PageData } from '../../api/Pagination/Types'
import { PaymentProviderValidation } from '../../api/PaymentProvider/Types/PaymentProviderValidation'
import { PaymentProviderAlternativeDto } from '../../api/Wallet/Types/PaymentProviderAlternativeDto'
import { CurrencyType } from '../../api/Wallet/Types/Wallet'
import { NPaymentProviderOrderingUpdateDeleteFormValues } from '../../routes/Administration/PaymentProviders/PaymentProviderOrderingForm'
import { ApiClient } from './ApiClient'
import { PageQuery, preparePageQueryParams } from './index'

export class NPaymentsApi {
  constructor(private apiClient: ApiClient) {}

  getClientBankAccounts: (
    clientId: string,
    pageIndex?: number,
    pageSize?: number
  ) => Promise<ClientBankAccount[]> = (clientId) => {
    return this.apiClient.get(`npayments/bank-details/client-accounts/client/${clientId}`)
  }
  addClientBankAccount: (data: AddClientBankAccount) => Promise<void> = (data) => {
    return this.apiClient.post(`npayments/bank-details/client-accounts`, data)
  }
  updateClientBankAccount: (id: string, data: UpdateClientBankAccount) => Promise<void> = (
    id,
    data
  ) => {
    return this.apiClient.put(`npayments/bank-details/client-accounts/${id}`, data)
  }
  deleteClientBankAccount: (id: string) => Promise<void> = (id) => {
    return this.apiClient.delete(`npayments/bank-details/client-accounts/${id}`)
  }
  setClientBankStatus: (id: string, statusId: ClientBankAccountStatus) => Promise<void> = (
    id,
    statusId
  ) => {
    return this.apiClient.put(`npayments/bank-details/client-accounts/${id}/status`, { statusId })
  }
  getBankAccountAuditLogs: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`npayments/bank-details/client-accounts/${id}/events`)
  }
  getClientTokenizedCards: (clientId: string) => Promise<TokenizedCardDetails[]> = (clientId) => {
    return this.apiClient.get(`npayments/card-details/${clientId}/tokenized-cards`)
  }
  getCardDetailsStatuses: () => Promise<NameDto[]> = () => {
    return this.apiClient.get(`npayments/card-details/card-statuses`)
  }
  updateCardDetailsCardholderName: (
    cardDetailsId: string,
    data: UpdateTokenizedCardholderName
  ) => Promise<void> = (cardDetailsId, data) => {
    return this.apiClient.put(`npayments/card-details/${cardDetailsId}/cardholder-name`, data)
  }
  updateCardDetailsStatus: (
    cardDetailsId: string,
    data: UpdateTokenizedCardStatus
  ) => Promise<void> = (cardDetailsId, data) => {
    return this.apiClient.put(`npayments/card-details/${cardDetailsId}/status`, data)
  }
  getCardDetailsAuditLogs: (cardDetailsId: string) => Promise<AuditLog[]> = (cardDetailsId) => {
    return this.apiClient.get(`npayments/card-details/${cardDetailsId}/events`)
  }
  getPaymentProvidersConfig: (query?: PageQuery) => Promise<PageData<NPaymentProviderDto[]>> = (
    query
  ) => {
    return this.apiClient.get(`npayments/mgmt/config?${preparePageQueryParams(query)}`)
  }
  getPaymentProviderEvents: (id: string) => Promise<AuditLog[]> = (id) => {
    return this.apiClient.get(`npayments/mgmt/config/${id}/events`)
  }

  getPaymentProvidersOrdering: (query?: PageQuery) => Promise<NPaymentProviderOrderDto> = (
    query
  ) => {
    return this.apiClient.get(`npayments/mgmt/methods/ordering?${preparePageQueryParams(query)}`)
  }
  updatePaymentProviderMethodOrder: (
    data: NPaymentProviderOrderingUpdateDeleteFormValues
  ) => Promise<void> = (data) => {
    return this.apiClient.put(`npayments/mgmt/methods/ordering`, data)
  }
  updatePaymentProviderMethodMultiOrdering: (
    data: NPaymentProviderOrderingUpdateDeleteFormValues[]
  ) => Promise<void> = (data) => {
    return this.apiClient.put(`npayments/mgmt/methods/multi-ordering`, {
      updateMethodOrderings: data,
    })
  }
  deletePaymentProviderMethodOrder: (
    data: NPaymentProviderOrderingUpdateDeleteFormValues
  ) => Promise<void> = (data) => {
    return this.apiClient.delete(`npayments/mgmt/methods/ordering`, data)
  }
  getPaymentProviderDetailConfig: (methodId: string) => Promise<NPaymentProviderDto> = (
    methodId
  ) => {
    return this.apiClient.get(`npayments/mgmt/config/${methodId}`)
  }
  updatePaymentProviderDetailConfig: (pspConfig: NPaymentProviderDto) => Promise<void> = (
    config
  ) => {
    return this.apiClient.put(`npayments/mgmt/config/${config.id}`, config)
  }

  getBankAccountDetailsValidationOptions: () => Promise<BankValidationDto> = () => {
    return this.apiClient.get(`/npayments/bank-details/validation-options`)
  }
  sendForValidation: (
    methodId?: string,
    email?: string,
    firstName?: string,
    lastName?: string
  ) => Promise<PaymentProviderValidation> = (methodId, email, firstName, lastName) => {
    const data = {
      methodId: methodId,
      userEmail: email,
      userFirstName: firstName,
      userLastName: lastName,
    }
    return this.apiClient.post('npayments/query', data)
  }

  getDeviceTypes: () => Promise<NameDto[]> = () => {
    return this.apiClient.get('npayments/lists/device-types')
  }

  getAlternativesPaymentProviders: (
    paymentProviderId: string,
    currencyId: CurrencyType
  ) => Promise<PaymentProviderAlternativeDto[]> = (paymentProviderId, currencyId) => {
    return this.apiClient.post(`/npayments/lists/method-alternatives`, {
      PaymentProviderMethodId: paymentProviderId,
      CurrencyId: currencyId,
    })
  }
}
